import { Database } from "@punchup/data/supabase.types";
import dayjs from "@punchup/utils/dayjs";
import { z } from "zod";

type AyrshareProfileRowBase =
  Database["public"]["Tables"]["ayrshare_user_profiles"]["Row"];
type AyrshareProfileInsertBase =
  Database["public"]["Tables"]["ayrshare_user_profiles"]["Insert"];
type AyrshareProfileUpdateBase =
  Database["public"]["Tables"]["ayrshare_user_profiles"]["Update"];

export type AyrshareProfileRow = Omit<
  AyrshareProfileRowBase,
  "social_accounts"
> & {
  social_accounts: SocialAccounts | null;
};
export type AyrshareProfileInsert = Omit<
  AyrshareProfileInsertBase,
  "social_accounts"
> & {
  social_accounts?: SocialAccounts | null;
};
export type AyrshareProfileUpdate = Omit<
  AyrshareProfileUpdateBase,
  "social_accounts"
> & {
  social_accounts?: SocialAccounts | null;
};

export type CrossPostRow =
  Database["public"]["Tables"]["ayrshare_social_posts"]["Row"];
export type CrossPostInsert =
  Database["public"]["Tables"]["ayrshare_social_posts"]["Insert"];
export type CrossPostUpdate =
  Database["public"]["Tables"]["ayrshare_social_posts"]["Update"];

export const SocialPlatformSchema = z.enum([
  "instagram",
  "tiktok",
  "youtube",
  "facebook",
  "x",
  // "punchup" is an in-house destination — submitting to it adds the video to
  // the comedian's own Punchup page rather than calling Ayrshare. It is always
  // treated as "linked" client-side and dispatched by the social/posts route
  // via insertComedianVideoUpload instead of Ayrshare.
  "punchup",
]);
export type SocialPlatform = z.infer<typeof SocialPlatformSchema>;

export const PlatformLinkStateSchema = z
  .object({
    state: z.enum(["linked", "unlinked", "needs_reauth"]),
    followerCount: z.number().nullable(),
    linkedAt: z.string().nullable(),
    lastCheckedAt: z.string().nullable(),
    displayName: z.string().nullable(),
    username: z.string().nullable().optional(),
  })
  .passthrough();
export type PlatformLinkState = z.infer<typeof PlatformLinkStateSchema>;

export const SocialAccountsSchema = z
  .object({
    tiktok: PlatformLinkStateSchema.optional(),
    youtube: PlatformLinkStateSchema.optional(),
    facebook: PlatformLinkStateSchema.optional(),
    instagram: PlatformLinkStateSchema.optional(),
    x: PlatformLinkStateSchema.optional(),
    // "punchup" is always synthesized client-side as "linked"; storing it here
    // is optional and not driven by Ayrshare.
    punchup: PlatformLinkStateSchema.optional(),
  })
  .passthrough();
export type SocialAccounts = z.infer<typeof SocialAccountsSchema> & {
  [platform: string]: PlatformLinkState | undefined;
};

// Ayrshare nests error info into HTTP 200 responses, so every boundary schema
// spreads these fields.
const ayrshareErrorFields = {
  status: z.enum(["success", "error"]).optional(),
  action: z.string().optional(),
  code: z.union([z.number(), z.string()]).optional(),
  message: z.string().optional(),
};

export const AyrshareProfileCreateResponseSchema = z
  .object({
    ...ayrshareErrorFields,
    profileKey: z.string().optional(),
    refId: z.string().optional(),
    title: z.string().optional(),
  })
  .passthrough();
export type AyrshareProfileCreateResponse = z.infer<
  typeof AyrshareProfileCreateResponseSchema
>;

// Ayrshare's /api/user returns both `displayNames` (recent) and
// `activeSocialAccounts`.
export const LinkedAccountSchema = z
  .object({
    platform: z.string(),
    connected: z.boolean().optional(),
    username: z.string().nullable().optional(),
    displayName: z.string().nullable().optional(),
    followerCount: z.number().nullable().optional(),
    userImage: z.string().nullable().optional(),
    needsReauth: z.boolean().optional(),
  })
  .passthrough();
export type LinkedAccount = z.infer<typeof LinkedAccountSchema>;

export const LinkedAccountsResponseSchema = z
  .object({
    ...ayrshareErrorFields,
    displayNames: z.array(z.unknown()).optional(),
    activeSocialAccounts: z.array(z.string()).optional(),
    socialAccounts: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough();
export type LinkedAccountsResponse = z.infer<
  typeof LinkedAccountsResponseSchema
>;

export const GenerateJwtResponseSchema = z
  .object({
    ...ayrshareErrorFields,
    url: z.string().url().optional(),
    token: z.string().optional(),
    expiresIn: z.union([z.number(), z.string()]).optional(),
  })
  .passthrough();
export type GenerateJwtResponse = z.infer<typeof GenerateJwtResponseSchema>;

// Ayrshare POST /api/analytics/social returns a top-level object keyed by
// platform, each with a nested `analytics` object whose follower field name
// varies per network (followersCount / followerCount / subscriberCount /
// fan_count / followers_count). `.catchall(z.unknown())` keeps the door open
// for new per-platform keys without breaking parsing.
export const SocialAnalyticsResponseSchema = z
  .object({
    ...ayrshareErrorFields,
  })
  .catchall(z.unknown());
export type SocialAnalyticsResponse = z.infer<
  typeof SocialAnalyticsResponseSchema
>;

export const UnlinkAccountResponseSchema = z
  .object({
    ...ayrshareErrorFields,
  })
  .passthrough();
export type UnlinkAccountResponse = z.infer<typeof UnlinkAccountResponseSchema>;

// Per-platform entries nested inside Ayrshare's POST /api/post response
// (`postIds[]` / `errors[]`). `code` may be string or number per Ayrshare docs.
// Not exported — callers consume the normalized PerPlatformResultSchema.
const AyrshareSuccessItemSchema = z
  .object({
    status: z.literal("success"),
    platform: z.string(),
    id: z.string(),
    postUrl: z.string().url().optional(),
  })
  .passthrough();

// Per-platform error entry inside Ayrshare's `postIds[]` or `errors[]`.
export const AyrshareErrorItemSchema = z
  .object({
    status: z.literal("error"),
    platform: z.string(),
    code: z.union([z.string(), z.number()]),
    message: z.string(),
    action: z.string().optional(),
  })
  .passthrough();

// Discriminated union used by `normalizePostIds` to parse `postIds[]` entries
// item-by-item — partial drift on one item won't fail the whole response.
export const AyrsharePostItemSchema = z.discriminatedUnion("status", [
  AyrshareSuccessItemSchema,
  AyrshareErrorItemSchema,
]);

// Schedule bounds — shared with the client picker (min/max + submit-disabled
// check) and the server superRefine below. The 10-minute lead time also
// doubles as the stale-row threshold in PostRow + PostDetailHeader,
// so changing it here flows through every consumer.
export const SCHEDULE_MIN_LEAD_MIN = 10;
export const SCHEDULE_MAX_ADVANCE_DAYS = 30;

// Optional payload consumed by the "punchup" platform branch in
// /api/v2/manager/social/posts. Required when "punchup" is in `platforms`.
export const PunchupPostInputSchema = z.object({
  title: z.string().min(3).max(100),
  thumbnailImgId: z.string().uuid().optional(),
});
export type PunchupPostInput = z.infer<typeof PunchupPostInputSchema>;

export const PostRequestSchema = z
  .object({
    comedianId: z.string().uuid(),
    idempotencyKey: z.string().uuid(),
    platforms: z.array(SocialPlatformSchema).min(1),
    mediaUrls: z.array(z.string().url()).min(0).max(10).default([]),
    content: z.string().min(1).max(2200),
    scheduledFor: z.string().datetime({ offset: true }).nullable().optional(),
    overrides: z.record(z.string(), z.unknown()).optional(),
    punchup: PunchupPostInputSchema.optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.scheduledFor) return;
    const target = dayjs.utc(data.scheduledFor);
    const now = dayjs.utc();
    if (target.isBefore(now.add(SCHEDULE_MIN_LEAD_MIN, "minute"))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["scheduledFor"],
        message: `Must be at least ${SCHEDULE_MIN_LEAD_MIN} minutes in the future`,
      });
    }
    if (target.isAfter(now.add(SCHEDULE_MAX_ADVANCE_DAYS, "day"))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["scheduledFor"],
        message: `Cannot schedule more than ${SCHEDULE_MAX_ADVANCE_DAYS} days in advance`,
      });
    }
  });
export type PostRequest = z.infer<typeof PostRequestSchema>;

// Security: this discriminated union explicitly lists ONLY public-safe fields.
// There is no `profileKey` variant, so downstream code literally cannot
// persist or return a Profile Key through this shape.
export const PerPlatformResultSchema = z.discriminatedUnion("status", [
  z.object({
    platform: SocialPlatformSchema,
    status: z.literal("success"),
    // Optional because the "punchup" branch (insertComedianVideoUpload) does
    // not go through Ayrshare and has no postUrl or ayrsharePostId.
    postUrl: z.string().url().optional(),
    ayrsharePostId: z.string().optional(),
    postedAt: z.string().nullable(),
    // Set only on the "punchup" branch — the comedian_video_uploads row id, so
    // the publish-pending-uploads cron can reconcile this entry back to the
    // cross-post row once a scheduled upload is released.
    uploadId: z.string().optional(),
  }),
  z.object({
    platform: SocialPlatformSchema,
    status: z.literal("error"),
    code: z.union([z.string(), z.number()]),
    message: z.string(),
  }),
  z.object({
    platform: SocialPlatformSchema,
    status: z.literal("pending"),
    // Set only on a scheduled "punchup" entry — see the success variant above.
    uploadId: z.string().optional(),
  }),
  z.object({
    platform: SocialPlatformSchema,
    status: z.literal("delete_not_supported"),
  }),
]);
export type PerPlatformResult = z.infer<typeof PerPlatformResultSchema>;

// HTTP is ALWAYS 200 — top-level `status` is the truth signal. `postIds[]`
// carries per-platform results when called with `Profile-Key`; `errors[]` may
// carry additional platform-level rejections. Callers must merge both into a
// single PerPlatformResult[].
export const PostResponseSchema = z
  .object({
    id: z.string().optional(),
    status: z.string().optional(),
    action: z.string().optional(),
    code: z.union([z.number(), z.string()]).optional(),
    message: z.string().optional(),
    refId: z.string().optional(),
    scheduleDate: z.string().optional(),
    postIds: z.array(z.unknown()).default([]),
    errors: z.array(z.unknown()).default([]),
    posts: z.array(z.unknown()).default([]),
  })
  .passthrough();
export type PostResponse = z.infer<typeof PostResponseSchema>;

export const DeletePostResponseSchema = z
  .object({
    // Spread first so the narrower `status` (string vs the generic enum) wins.
    ...ayrshareErrorFields,
    status: z.string().optional(),
    id: z.string().optional(),
    message: z.string().optional(),
    posts: z
      .array(
        z
          .object({
            platform: z.string(),
            status: z.string(),
          })
          .passthrough()
      )
      .optional(),
  })
  .passthrough();
export type DeletePostResponse = z.infer<typeof DeletePostResponseSchema>;

// One schema fits every webhook action type; the handler dispatches on
// `action` and 200+breadcrumbs unknown values. `.passthrough()` so Ayrshare
// adding fields doesn't break ingest.
export const WebhookPayloadSchema = z
  .object({
    action: z.string(),
    created: z.string(),
    code: z.number().optional(),
    refId: z.string().optional(),
    url: z.string().optional(),
    // scheduled-specific
    status: z.string().optional(),
    id: z.string().optional(),
    postIds: z.array(z.record(z.string(), z.unknown())).optional(),
    errors: z.array(z.record(z.string(), z.unknown())).optional(),
    // social-specific
    platform: z.string().optional(),
    type: z.string().optional(),
    source: z.string().optional(),
    title: z.string().optional(),
    displayName: z.string().optional(),
  })
  .passthrough();
export type WebhookPayload = z.infer<typeof WebhookPayloadSchema>;

export const PaginationSchema = z.object({
  page: z.number().int().min(0),
  pageSize: z.number().int().min(1).max(100),
  total: z.number().int().min(0),
  hasMore: z.boolean(),
});
export type Pagination = z.infer<typeof PaginationSchema>;

// `posts` uses z.unknown().array() + runtime cast to CrossPostRow[] because
// the Supabase Row type includes jsonb fields whose shape is modeled
// separately via PerPlatformResultSchema. The TS type below narrows for
// consumers.
export const PaginatedCrossPostsResponseSchema = z.object({
  posts: z.array(z.unknown()),
  pagination: PaginationSchema,
});
export type PaginatedCrossPostsResponse = {
  posts: CrossPostRow[];
  pagination: Pagination;
};

// Per-post engagement. Server normalizes Ayrshare's platform-keyed response
// into this typed shape. `twitter` → `x` normalization happens at the route
// boundary so downstream consumers never see Ayrshare's legacy key.
const PostAnalyticsPlatformMetricsSchema = z
  .object({
    views: z.number().nullable().optional(),
    likes: z.number().nullable().optional(),
    comments: z.number().nullable().optional(),
    shares: z.number().nullable().optional(),
    impressions: z.number().nullable().optional(),
  })
  .passthrough();

export const PostAnalyticsItemSchema = z.object({
  platform: SocialPlatformSchema,
  metrics: PostAnalyticsPlatformMetricsSchema,
  analyticsUrl: z.string().url().optional(),
});
export type PostAnalyticsItem = z.infer<typeof PostAnalyticsItemSchema>;
export type PostAnalytics = PostAnalyticsItem[];
